import { apiV1 } from './api'

/**
 * Media service for the admin app.
 *
 * Talks to the non-versioned `/api/media/*` endpoints (the backend exposes
 * `/api/media/upload-url` independent of /v3) and gives admin-side pages a
 * tiny helper to upload a file end-to-end and get back the storage key + URL.
 */

const MEDIA_BASE_URL = (import.meta.env.VITE_MEDIA_BASE_URL as string)?.replace(/\/$/, '') || ''

export type MediaCategory =
  | 'logo'
  | 'coverImage'
  | 'gallery'
  | 'introVideo'
  | 'videoGallery'
  | 'courseImage'
  | 'courseVideo'
  | 'generalImage'
  | 'generalVideo'

export interface SignedUrlResponse {
  uploadUrl: string
  fileKey: string
  cloudFrontUrl: string
}

/** Build a display URL from a stored fileKey. */
export function getMediaUrlFromKey(keyOrUrl: string): string {
  if (!keyOrUrl) return ''
  if (keyOrUrl.startsWith('http://') || keyOrUrl.startsWith('https://')) return keyOrUrl
  if (!MEDIA_BASE_URL) return keyOrUrl
  return `${MEDIA_BASE_URL}/${keyOrUrl}`
}

/**
 * Extract a storage file key from either a raw key or a public media URL.
 * Mirrors `yoga_school_admin_dashboard/src/services/mediaService.ts` so room /
 * course media that was saved as a full URL still resolves correctly.
 */
export function extractFileKeyFromUrl(value: string): string | null {
  try {
    if (!value) return null
    if (!value.startsWith('http://') && !value.startsWith('https://')) return value
    const parsedUrl = new URL(value)
    const pathname = parsedUrl.pathname.startsWith('/')
      ? parsedUrl.pathname.slice(1)
      : parsedUrl.pathname
    return pathname || null
  } catch (error) {
    console.error('Error extracting fileKey from URL:', error)
    return null
  }
}

async function getUploadUrl(
  fileName: string,
  fileType: string,
  fileSize: number,
  category: MediaCategory,
  schoolId?: string,
  courseId?: string,
): Promise<SignedUrlResponse> {
  // Endpoint is /api/media/upload-url (no v3 prefix). apiV1 already strips /v3.
  const response = await apiV1.post('/media/upload-url', {
    fileName,
    fileType,
    fileSize,
    category,
    ...(schoolId ? { schoolId } : {}),
    ...(courseId ? { courseId } : {}),
  })
  return response.data?.data as SignedUrlResponse
}

/** Direct PUT to the presigned URL with progress callbacks. */
function uploadToPresignedUrl(
  file: File,
  uploadUrl: string,
  onProgress?: (percent: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded * 100) / e.total))
      }
    })
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`))
      }
    })
    xhr.addEventListener('error', () => reject(new Error('Upload failed: network error')))
    xhr.open('PUT', uploadUrl, true)
    xhr.setRequestHeader('Content-Type', file.type)
    xhr.send(file)
  })
}

/**
 * One-shot helper: ask backend for a presigned URL, PUT the file, return the
 * stored `fileKey` and a display URL.
 *
 * Pass `schoolId` / `courseId` when you want the file organised under those
 * prefixes in object storage (matches how the school dashboard uploads).
 */
export async function uploadFile(
  file: File,
  category: MediaCategory,
  schoolIdOrOnProgress?: string | ((percent: number) => void),
  courseId?: string,
  onProgress?: (percent: number) => void,
): Promise<{ fileKey: string; url: string }> {
  // Back-compat: old callers invoked uploadFile(file, category, onProgress).
  const schoolId = typeof schoolIdOrOnProgress === 'string' ? schoolIdOrOnProgress : undefined
  const progress = typeof schoolIdOrOnProgress === 'function' ? schoolIdOrOnProgress : onProgress

  const signed = await getUploadUrl(file.name, file.type, file.size, category, schoolId, courseId)
  await uploadToPresignedUrl(file, signed.uploadUrl, progress)
  return {
    fileKey: signed.fileKey,
    url: signed.cloudFrontUrl || getMediaUrlFromKey(signed.fileKey),
  }
}

/** Upload a course image to the media bucket and return its storage fileKey. */
export async function uploadCourseImage(
  file: File,
  schoolId: string,
  courseId?: string,
  onProgress?: (percent: number) => void,
): Promise<{ success: boolean; data: { fileKey: string } }> {
  const { fileKey } = await uploadFile(file, 'courseImage', schoolId, courseId, onProgress)
  return { success: true, data: { fileKey } }
}

/** Upload a course video to the media bucket and return its storage fileKey. */
export async function uploadCourseVideo(
  file: File,
  schoolId: string,
  courseId?: string,
  onProgress?: (percent: number) => void,
): Promise<{ success: boolean; data: { fileKey: string } }> {
  const { fileKey } = await uploadFile(file, 'courseVideo', schoolId, courseId, onProgress)
  return { success: true, data: { fileKey } }
}

export default {
  getMediaUrlFromKey,
  extractFileKeyFromUrl,
  uploadFile,
  uploadCourseImage,
  uploadCourseVideo,
}
